<template>
    <div>
        <div v-if="loading" class="text-center p-5"><b-spinner variant="info" role="status" /></div>
        <b-alert v-else-if="loadFailed" show variant="danger">
            {{ $t('message.unknownError') }}
            <b-button size="sm" variant="outline-danger" @click="load">{{ $t('label.reset') }}</b-button>
        </b-alert>
        <b-form v-else @submit.prevent="sportClubUUID ? requestUpdate() : requestCreate()">
            <fieldset :disabled="!record.status.save">
                <div class="row">
                    <div class="col-lg-7">
                        <b-form-group :label="$t('label.name')" label-for="sport-club-name">
                            <b-form-input id="sport-club-name" v-model.trim="createForm.name" required maxlength="100" />
                        </b-form-group>
                        <b-form-group :label="$t('module.sportClubSportType')" label-for="sport-club-type">
                            <multiselect id="sport-club-type" v-model="createForm.sportType" :options="sportTypes" label="name" track-by="uuid"
                                placeholder="Select sport type" :allow-empty="false" :disabled="!record.status.save" />
                        </b-form-group>
                        <b-form-group :label="$t('label.description')">
                            <ckeditor :editor="editor" v-model="createForm.description" :config="editorConfig" :disabled="!record.status.save" />
                        </b-form-group>
                    </div>
                    <div class="col-lg-5">
                        <b-form-group label="Cover image" label-for="sport-club-cover">
                            <img v-if="preview || createForm.coverImageUrl" :src="preview || createForm.coverImageUrl" alt="Cover" class="cover-preview mb-3" />
                            <b-form-file id="sport-club-cover" v-model="createForm.coverImage" accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
                                placeholder="Choose an image..." browse-text="Browse" @input="previewImage" />
                            <b-button v-if="createForm.coverImage" size="sm" variant="link" @click="clearImage">Clear</b-button>
                        </b-form-group>
                    </div>
                </div>
                <hr />
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <h5 class="mb-0">{{ $t('module.sportClubBoardType') }}</h5>
                    <b-button size="sm" variant="outline-info" :disabled="!canAddBoard" @click="addBoard">
                        <i class="mdi mdi-plus" aria-hidden="true"></i> {{ $t('label.addX', ['']) }}
                    </b-button>
                </div>
                <div v-for="(board, index) in createForm.boards" :key="board.key" class="row align-items-start border rounded mx-0 p-3 mb-3">
                    <div class="col-md-6">
                        <b-form-group label="Member" :label-for="`club-member-${board.key}`">
                            <multiselect :id="`club-member-${board.key}`" v-model="board.user" :options="memberOptions(board)" label="name" track-by="uuid"
                                placeholder="Select member" :allow-empty="false" :disabled="!record.status.save" />
                        </b-form-group>
                    </div>
                    <div class="col-md-4">
                        <b-form-group label="Board Role" :label-for="`club-role-${board.key}`">
                            <multiselect :id="`club-role-${board.key}`" v-model="board.boardType" :options="boardTypes(board)" label="name" track-by="uuid"
                                placeholder="Select role" :allow-empty="false" :disabled="!record.status.save" />
                        </b-form-group>
                    </div>
                    <div class="col-md-2 pt-md-4">
                        <b-button size="sm" variant="outline-danger" :disabled="!record.status.save" @click="createForm.boards.splice(index, 1)">
                            <i class="mdi mdi-delete" aria-hidden="true"></i> {{ $t('label.deleteX', []) }}
                        </b-button>
                    </div>
                </div>
                <b-alert :show="!!validationError" variant="danger">{{ validationError }}</b-alert>
                <div class="d-flex justify-content-end border-top pt-3">
                    <b-button variant="light" class="mr-2" @click="$emit('done')">{{ $t('label.cancel') }}</b-button>
                    <b-button type="submit" variant="info" :disabled="!record.status.save">
                        <b-spinner v-if="!record.status.save" small class="mr-1" />{{ sportClubUUID ? $t('label.updateX', []) : $t('label.createX', []) }}
                    </b-button>
                </div>
            </fieldset>
        </b-form>
    </div>
</template>
<script>
import CKEditor from '@ckeditor/ckeditor5-vue';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Multiselect from 'vue-multiselect';
import { sportClubMethods } from '@/state/api';

function includeSelected(options, selected) {
    if (!selected || !Array.isArray(options)) return options || [];
    if (options.some(item => item && item.uuid === selected.uuid)) return options;
    return [...options, selected];
}

export default {
    components: { Multiselect, ckeditor: CKEditor.component },
    props: { sportClubUUID: { type: String, default: '' }, searchCriteria: { type: Object, required: true } },
    data() {
        return {
            loading: true, loadFailed: false, validationError: '', preview: '', nextBoardKey: 0,
            editor: ClassicEditor,
            editorConfig: { toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'undo', 'redo'] },
            createForm: { name: '', description: '', sportType: null, coverImage: null, coverImageUrl: '', boards: [] },
        };
    },
    computed: {
        record() { return this.$store.state.api.sportClub; },
        canAddBoard() {
            const boardTypes = this.record?.options?.boardTypes;
            const users = this.record?.options?.users;
            return Boolean(boardTypes && boardTypes.length && users && users.length);
        },
        sportTypes() { return includeSelected(this.record?.options?.sportTypes || [], this.createForm.sportType); },
    },
    watch: {
        sportClubUUID: {
            handler() {
                this.load();
            },
        },
    },
    created() { this.load(); },
    beforeDestroy() { if (this.preview) URL.revokeObjectURL(this.preview); },
    methods: {
        ...sportClubMethods,
        async load() {
            this.loading = true;
            this.loadFailed = false;
            const [options, details] = await Promise.all([
                this.loadSportClubOptions(),
                this.sportClubUUID ? this.getSportClub({ uuid: this.sportClubUUID }) : Promise.resolve(true),
            ]);
            this.loadFailed = !options || !details;
            if (!this.loadFailed && this.sportClubUUID) {
                const club = this.record?.details;
                if (club) {
                    this.createForm = {
                        name: club.name || '',
                        description: club.description || '',
                        sportType: club.sportType ? { uuid: club.sportType.uuid, name: club.sportType.name } : null,
                        coverImage: null,
                        coverImageUrl: club.coverImage || club.url || '',
                        boards: (club.boards || []).map(board => ({
                            key: this.nextBoardKey++,
                            uuid: board.uuid,
                            boardType: board.boardType ? { uuid: board.boardType.uuid, name: board.boardType.name } : null,
                            user: board.user ? {
                                uuid: board.user.uuid,
                                name: board.user.fullName || [board.user.firstName, board.user.lastName].filter(Boolean).join(' ') || board.user.email || '',
                            } : null,
                        })),
                    };
                }
            } else if (!this.sportClubUUID) {
                this.createForm = {
                    name: '',
                    description: '',
                    sportType: null,
                    coverImage: null,
                    coverImageUrl: '',
                    boards: [],
                };
            }
            this.loading = false;
        },
        addBoard() { this.createForm.boards.push({ key: this.nextBoardKey++, uuid: '', user: null, boardType: null }); },
        boardTypes(board) { return includeSelected(this.record?.options?.boardTypes || [], board.boardType); },
        memberOptions(board) {
            const selectedElsewhere = this.createForm.boards.filter(item => item !== board && item.user).map(item => item.user.uuid);
            return includeSelected(this.record?.options?.users || [], board.user).filter(user => !selectedElsewhere.includes(user.uuid));
        },
        previewImage(file) {
            if (this.preview) URL.revokeObjectURL(this.preview);
            this.preview = '';
            this.validationError = '';
            if (!file) return;
            if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
                this.validationError = 'Please select a valid image file (JPEG, PNG, GIF, WebP, BMP up to 5MB).';
                return;
            }
            this.preview = URL.createObjectURL(file);
        },
        clearImage() {
            this.createForm.coverImage = null;
            this.previewImage(null);
        },
        requestCreate() { return this.save(); },
        requestUpdate() { return this.save(); },
        async save() {
            if (!this.record.status.save) return;
            this.validationError = '';
            if (!this.createForm.name.trim() || this.createForm.name.length > 100 || !this.createForm.sportType) {
                this.validationError = 'Please enter a name and select a sport type.';
                return;
            }
            if (!this.sportClubUUID && !this.createForm.coverImage) {
                this.validationError = 'Cover image is required.';
                return;
            }
            const file = this.createForm.coverImage;
            if (file && (!['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'].includes(file.type) || file.size > 5 * 1024 * 1024)) {
                this.validationError = 'Please select a valid image file (JPEG, PNG, GIF, WebP, BMP up to 5MB).';
                return;
            }
            if (this.createForm.boards.some(board => !board.user || !board.boardType)) {
                this.validationError = 'Please select both member and role for each board entry.';
                return;
            }
            const users = this.createForm.boards.map(board => board.user.uuid);
            if (new Set(users).size !== users.length) {
                this.validationError = 'Duplicate board member detected.';
                return;
            }
            const data = {
                uuid: this.sportClubUUID, name: this.createForm.name.trim(), description: this.createForm.description,
                sportTypeUUID: this.createForm.sportType.uuid, coverImage: file,
                boards: this.createForm.boards.map(board => ({ uuid: board.uuid, boardTypeUUID: board.boardType.uuid, userUUID: board.user.uuid })),
            };
            const result = await (this.sportClubUUID ? this.updateSportClub : this.createSportClub)({ data, filter: { ...this.searchCriteria } });
            if (result) this.$emit('done');
        },
    },
};
</script>
<style scoped>
.cover-preview { width: 100%; max-height: 220px; object-fit: contain; background: #f5f7fa; border-radius: 8px; }
</style>
